// =============================================================================
// PROGRAMA DO PRESTADOR (DA-435)
// =============================================================================
// O prestador passa orcamento ao cliente com mao de obra + material. Ele quer ganhar tambem no
// material — esta cotando, buscando e respondendo pela troca, e isso custa —, mas o cliente nao
// quer pagar a mais pela compra. O mecanismo: ele leva R$ 1.000 de material, informa R$ 1.200 ao
// cliente, a nota sai por R$ 1.200, entram R$ 1.200, e a loja devolve a diferenca (menos o Custo
// da Operacao) como CREDITO DE LOJA — resgatavel so aqui, e por isso fidelizacao, nao desconto.
//
// A conta mora aqui, em UMA copia, porque vale nos dois lados: o caixa PREVE o credito antes de
// finalizar (o operador precisa poder dizer ao prestador quanto vai sobrar) e o backend GERA de
// verdade. Duas copias divergiriam no primeiro ajuste da aliquota — e divergir aqui e' divergir
// num numero que o prestador confere com o contador dele.
//
// Toda aritmetica roda em CENTAVOS INTEIROS (DA-418): dinheiro tem 2 casas, entao comparar com
// epsilon abaixo da unidade da moeda inventa regra de negocio onde deveria haver arredondamento.

const cents = (v: number): number => Math.round(v * 100);
const reais = (c: number): number => c / 100;

// -----------------------------------------------------------------------------
// A conta do credito
// -----------------------------------------------------------------------------

export interface CalculoPrograma {
  /** Preco correto da forma de pagamento, congelado no fechamento. */
  valorOriginal: number;
  /** O que o prestador informou ao cliente dele (ABSOLUTO, nunca percentual). */
  valorInformado: number;
  /** valorInformado - valorOriginal. Zero quando nao houve markup. */
  diferenca: number;
  /** A retencao da loja sobre a diferenca. */
  custoOperacao: number;
  /** O que de fato vira credito pro prestador. */
  valorCredito: number;
  /** Aliquota aplicada, pra congelar junto com o valor que ela produziu. */
  custoOperacaoPct: number;
}

/**
 * Calcula o que o prestador recebe de credito.
 *
 * `custoOperacaoPct` e' o "Custo da Operacao" — nome escolhido pelo Ruan de proposito. NAO chamar
 * de imposto: o prestador tem contador e faz conta, e uma retencao que nao confere com a aliquota
 * real derruba a confianca de um programa que so vive de confianca.
 *
 * O credito e' `diferenca - custoOperacao` (subtracao, nao um segundo percentual), pra que as duas
 * parcelas somem EXATAMENTE a diferenca — a mesma maquina de arredondamento nos dois lados da
 * conta, senao sobra fracao de centavo que ninguem ve e alguem tira conclusao dela (DA-418).
 *
 * Valor informado ABAIXO do original nao e' markup negativo: e' desconto comum. Devolve tudo zero
 * (nenhum credito nasce), e o desconto segue pelo caminho normal do sistema.
 */
export function calcularCreditoPrograma(
  valorOriginal: number,
  valorInformado: number,
  custoOperacaoPct: number,
): CalculoPrograma {
  const origCents = cents(valorOriginal);
  const infoCents = cents(valorInformado);
  const difCents = infoCents - origCents;

  if (difCents <= 0) {
    return {
      valorOriginal: reais(origCents),
      valorInformado: reais(infoCents),
      diferenca: 0,
      custoOperacao: 0,
      valorCredito: 0,
      custoOperacaoPct,
    };
  }

  const pct = Number.isFinite(custoOperacaoPct) ? Math.max(0, Math.min(100, custoOperacaoPct)) : 0;
  const custoCents = Math.round((difCents * pct) / 100);
  const creditoCents = difCents - custoCents;

  return {
    valorOriginal: reais(origCents),
    valorInformado: reais(infoCents),
    diferenca: reais(difCents),
    custoOperacao: reais(custoCents),
    valorCredito: reais(creditoCents),
    custoOperacaoPct: pct,
  };
}

// -----------------------------------------------------------------------------
// O teto de markup
// -----------------------------------------------------------------------------

/**
 * Le o teto guardado em `app_settings.programa_prestador_teto_markup_pct`.
 *
 * String vazia (o valor semeado) significa SEM LIMITE. O teto nasce como parametro configuravel
 * justamente pra que "depois a gente ajusta" possa acontecer: sem o parametro, ajustar viraria
 * uma mudanca de codigo, e por isso nunca aconteceria. O numero real sai da distribuicao observada
 * em `programa_atribuicoes` dali a 1-2 meses.
 */
export function parseTetoMarkup(valor: string | null | undefined): number | null {
  const t = (valor ?? '').trim().replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

// -----------------------------------------------------------------------------
// GASTAR o credito (DA-436) — a carteira e o teto de uso
// -----------------------------------------------------------------------------

/**
 * Qual carteira uma linha de Credito da Loja consome.
 *
 * A carteira e' UNICA no banco (`creditos_cliente`), classificada por `origem_tipo` — mas o
 * CONSUMO precisa escolher um lado, porque uma compra aceita um unico credito (decisao dele): usou
 * o de troca, nao aceita o do programa, e vice-versa. Some o caso misto, e com ele some a pergunta
 * "qual dos dois a nota deve citar".
 */
export type CarteiraCredito = 'troca' | 'programa';

/**
 * NULL/desconhecido le como 'troca'.
 *
 * Nao e' tolerancia a dado ruim: e' o default correto. Todo credito que existia antes do programa
 * e' de troca, e um pedido que ja estava na fila do caixa durante a janela do deploy chega aqui sem
 * o campo. Ler NULL como 'programa' abriria uma porta que ninguem escolheu.
 */
export function normalizarCarteira(v: string | null | undefined): CarteiraCredito {
  return (v ?? '').trim().toLowerCase() === 'programa' ? 'programa' : 'troca';
}

/**
 * Le o teto de uso guardado em `app_settings.programa_prestador_teto_uso_pct` (semeado em 30).
 *
 * NAO delega pro `parseTetoMarkup`: os dois tratam o **zero** de formas opostas, e confundi-los
 * inverte a regra. Num teto de MARKUP, 0 nao tem uso pratico e cai no "sem limite" junto com o
 * lixo. Num teto de USO, `0` e' o jeito mais intuitivo de escrever **"suspende o gasto do credito
 * do programa"** — e delegar fazia exatamente o contrario: liberava 100% da compra.
 *
 * Continua valendo: string vazia (ou ilegivel, ou negativa) = SEM LIMITE. Chutar um teto que
 * ninguem configurou barraria uma venda legitima no balcao, e a trava de venda 100% credito que ja
 * existe no caixa segue de pe de qualquer jeito.
 */
export function parseTetoUso(valor: string | null | undefined): number | null {
  const t = (valor ?? '').trim().replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

/**
 * Qual carteira propor quando ninguem escolheu ainda.
 *
 * Como uma compra aceita um unico credito, o **maximo abativel nao e' a soma** dos dois saldos — e'
 * o maior deles. Foi assim que a tela de Enviar ao Caixa chegou a anunciar "Saldo disponivel
 * R$ 500,00" pra um cliente com R$ 100 de troca e R$ 400 do programa: a vendedora prometia R$ 500
 * de abatimento ao cliente e o caixa so conseguia entregar R$ 100.
 *
 * Regra: o lado com MAIS saldo, e 'troca' no empate (inclusive zero a zero) — o comportamento de
 * sempre. Quem quiser o outro lado troca no seletor.
 */
export function carteiraPadrao(saldoTroca: number, saldoPrograma: number): CarteiraCredito {
  return cents(saldoPrograma) > cents(saldoTroca) ? 'programa' : 'troca';
}

/** O maximo que da pra abater numa compra: o maior saldo, NUNCA a soma. */
export function saldoAbativel(saldoTroca: number, saldoPrograma: number): number {
  return reais(Math.max(cents(saldoTroca), cents(saldoPrograma), 0));
}

/**
 * Quanto do credito do PROGRAMA cabe nesta compra.
 *
 * Base = total da venda ANTES do abate (o que o cliente esta comprando), nao o que sobra depois —
 * senao o teto se morderia: abater mudaria a base que autoriza o abate.
 *
 * So vale pro credito do programa. O credito de TROCA e' dinheiro do proprio cliente voltando, e
 * limitar o gasto dele seria hostil sem que ninguem tenha pedido. As duas razoes do teto sao do
 * mecanismo do programa: venda 100% credito nao emite nota (DA-369) e sem nota o credito seguinte
 * nao tem onde virar desconto; e o que prende o prestador e' SALDO PARADO aqui — a compra tem que
 * deixar sobra, senao o premio vira desconto comum, que nao prende ninguem.
 *
 * Sem teto (null) devolve o proprio total: cabe tudo, e o bloqueio de venda 100% credito que ja
 * existe no caixa segue sendo a unica trava.
 */
export function tetoUsoPrograma(totalVenda: number, tetoPct: number | null): number {
  const totalCents = cents(totalVenda);
  if (totalCents <= 0) return 0;
  if (tetoPct === null) return reais(totalCents);
  // Arredonda PRA BAIXO: o teto e' um limite, e passar dele por meio centavo por causa de
  // arredondamento seria uma regra de negocio nascida de aritmetica (DA-418).
  return reais(Math.floor((totalCents * Math.max(0, Math.min(100, tetoPct))) / 100));
}

/** true quando o credito do programa pedido passa do teto de uso desta compra. */
export function usoProgramaAcimaDoTeto(
  valorCredito: number,
  totalVenda: number,
  tetoPct: number | null,
): boolean {
  if (tetoPct === null) return false;
  return cents(valorCredito) > cents(tetoUsoPrograma(totalVenda, tetoPct));
}

/** true quando o markup pedido passa do teto configurado. Sem teto (null) nunca estoura. */
export function markupAcimaDoTeto(
  valorOriginal: number,
  valorInformado: number,
  tetoPct: number | null,
): boolean {
  if (tetoPct === null) return false;
  const origCents = cents(valorOriginal);
  if (origCents <= 0) return false;
  const difCents = cents(valorInformado) - origCents;
  if (difCents <= 0) return false;
  return (difCents / origCents) * 100 > tetoPct;
}

/**
 * O maior valor informado que o teto autoriza pra esta base. `null` quando nao ha teto.
 *
 * Existe pra a recusa poder dizer o NUMERO, nao so "passou do teto": quem esta barrada e' a
 * vendedora com o prestador na frente dela, e mandar ela adivinhar por tentativa transforma uma
 * regra de negocio em jogo. Arredonda PRA BAIXO — o teto e' limite, e passar dele por causa de
 * arredondamento seria regra nascida de aritmetica (DA-418).
 */
export function tetoMarkupValor(valorOriginal: number, tetoPct: number | null): number | null {
  if (tetoPct === null) return null;
  const origCents = cents(valorOriginal);
  if (origCents <= 0) return null;
  // Multiplica ANTES de dividir: `origCents * (1 + pct/100)` passa por um float intermediario e
  // 100000 * 1,15 vira 114999,999..., que o floor derruba pra um centavo abaixo do teto real — o
  // numero que a recusa anuncia como maximo permitido tem que SER permitido (DA-418).
  return reais(Math.floor((origCents * (100 + Math.max(0, tetoPct))) / 100));
}

/** O markup em % sobre a base, pra exibicao e pra calibrar o teto depois. 0 quando nao ha markup. */
export function markupPct(valorOriginal: number, valorInformado: number): number {
  const origCents = cents(valorOriginal);
  if (origCents <= 0) return 0;
  const difCents = cents(valorInformado) - origCents;
  if (difCents <= 0) return 0;
  return Math.round((difCents / origCents) * 10000) / 100;
}

// -----------------------------------------------------------------------------
// A forma de pagamento que serviu de base
// -----------------------------------------------------------------------------

export type BaseForma = 'pix' | 'cartao' | 'misto' | 'dinheiro';

/**
 * Classifica a forma de pagamento da venda pra guardar na atribuicao.
 *
 * Serve pra MEDIR um efeito colateral aceito de olho aberto: como a base do pix e' menor (o pix ja
 * vem com desconto), o MESMO valor informado gera MAIS credito no pix — e quem escolhe a forma e'
 * o proprio prestador. Guardar isso e' o que permite ver o desvio na tabela em vez de descobri-lo
 * pela margem la na frente.
 *
 * `tipos` sao os `formas_pagamento.tipo` das linhas com valor > 0.
 */
export function classificarBaseForma(tipos: Array<string | null | undefined>): BaseForma {
  const t = new Set(tipos.map((x) => (x ?? '').trim().toLowerCase()).filter(Boolean));
  const temPix = t.has('pix');
  const temCartao = t.has('cartao_credito') || t.has('cartao_debito');
  if (temPix && temCartao) return 'misto';
  if (temPix) return 'pix';
  if (temCartao) return 'cartao';
  if (t.size > 1) return 'misto';
  return 'dinheiro';
}
